import { createContext, PropsWithChildren, ReactNode, useContext } from "react";
import { twMerge } from "tailwind-merge";

import { ChevronIcon, SectionHeader } from "~/common/components";
import { DIRECTIONS } from "~/common/constants";
import { replaceUnderscoresUpperCase } from "~/common/utils";
import {
  ExpandableSectionsTitleCodes,
  ExpandableWrapperTitleCodes,
} from "~/common/utils/types/summariesExpandableIdsTypes";
import { usePreviewStore } from "~/providers/stores/usePreviewStore";
import { PreviewSectionLayout } from "./PreviewSectionLayout";

interface WrapperProps extends PropsWithChildren {
  code: ExpandableWrapperTitleCodes;
  className?: string;
  expandableSections: string[];
  title?: string | ReactNode;
}

interface ExpandableContextType {
  wrapperCode: ExpandableWrapperTitleCodes;
}

interface SectionProps extends PropsWithChildren {
  code: ExpandableSectionsTitleCodes;
  description?: string;
  icon?: ReactNode;
  className?: string;
  titleClassName?: string;
}

const ExpandableContext = createContext<ExpandableContextType | null>(null);

export const useExpandableContext = () => {
  const context = useContext(ExpandableContext);

  if (!context)
    throw new Error("Section must be used as a children of Wrapper");
  return context;
};

export const Wrapper = ({
  code,
  children,
  className,
  expandableSections,
  title,
}: WrapperProps) => {
  const { expandedSections, setExpandedSections } = usePreviewStore();

  const handleExpandAll = () =>
    setExpandedSections({ code: code, sections: expandableSections });

  const handleCollapseAll = () =>
    setExpandedSections({ code: code, sections: [] });

  const isExpandedAll =
    !!expandedSections[code].length &&
    expandableSections.every((id) => expandedSections[code].includes(id));

  return (
    <ExpandableContext.Provider
      value={{
        wrapperCode: code,
      }}
    >
      <PreviewSectionLayout
        title={title ?? replaceUnderscoresUpperCase(code)}
        className={className}
        actions={
          <div className="flex items-center justify-end gap-2">
            <button
              onClick={handleCollapseAll}
              className={twMerge(!isExpandedAll && "underline")}
            >
              Collapse all
            </button>
            <p>/</p>
            <button
              onClick={handleExpandAll}
              className={twMerge(isExpandedAll && "underline")}
            >
              Expand all
            </button>
          </div>
        }
      >
        {children}
      </PreviewSectionLayout>
    </ExpandableContext.Provider>
  );
};

const Section = ({
  code,
  description,
  icon,
  className,
  children,
  titleClassName,
}: SectionProps) => {
  const { wrapperCode } = useExpandableContext();
  const { expandedSections, setExpandedSections } = usePreviewStore();

  const isExpanded = expandedSections[wrapperCode].includes(code);

  const handleExpandCollapse = () => {
    if (isExpanded) {
      setExpandedSections({
        code: wrapperCode,
        sections: expandedSections[wrapperCode].filter((id) => id !== code),
      });
    } else {
      setExpandedSections({
        code: wrapperCode,
        sections: [...expandedSections[wrapperCode], code],
      });
    }
  };

  return (
    <div className={twMerge("border-b border-neutral-80 pb-4", className)}>
      <button
        className="flex w-full items-center justify-between"
        onClick={handleExpandCollapse}
      >
        <div className="flex w-full">
          <div
            className={twMerge(
              "w-full",
              icon && "flex items-center gap-3",
              description && "md:w-1/4",
            )}
          >
            {icon}
            <SectionHeader
              className={twMerge(
                "text-start text-lg font-normal",
                titleClassName,
              )}
            >
              {replaceUnderscoresUpperCase(code)}
            </SectionHeader>
          </div>
          <p className="flex items-center text-right text-sm text-gray-60 first-letter:capitalize">
            {description}
          </p>
        </div>
        <ChevronIcon
          className="ml-3 size-2.5 cursor-pointer text-gray-10"
          direction={isExpanded ? DIRECTIONS.top : DIRECTIONS.down}
        />
      </button>
      {isExpanded && <div className="pt-3">{children}</div>}
    </div>
  );
};

const Expandable = {
  Wrapper,
  Section,
};

export default Expandable;
